import {
  schools,
  getSchoolColor,
  computeEnrollmentBySchool,
  computeAvgDistanceBySchool,
} from './data';
import type { MapState } from './types';

interface SchoolPanelProps {
  state: MapState;
  onToggleSchool: (schoolName: string) => void;
}

export function SchoolPanel({ state, onToggleSchool }: SchoolPanelProps) {
  const enrollment = computeEnrollmentBySchool(
    state.areaAssignments,
    state.selectedYear,
  );
  const avgDist = computeAvgDistanceBySchool(
    state.areaAssignments,
    state.selectedYear,
  );

  const totalStudents = Object.values(enrollment).reduce((a, b) => a + b, 0);
  const openSchools = schools.filter((s) => state.schoolStatus[s.name]);
  const closedSchools = schools.filter((s) => !state.schoolStatus[s.name]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-davis-slate uppercase tracking-wide">
          Schools ({state.selectedYear})
        </div>
        <div className="text-xs text-davis-slate">
          {openSchools.length}/{schools.length} open &middot;{' '}
          <span className="font-semibold">{totalStudents.toLocaleString()}</span> students
        </div>
      </div>

      <div className="space-y-0.5">
        {schools.map((school) => {
          const isOpen = state.schoolStatus[school.name] ?? true;
          const color = getSchoolColor(school.name);
          const students = enrollment[school.name] ?? 0;
          const distance = avgDist[school.name] ?? 0;

          return (
            <button
              key={school.name}
              onClick={() => onToggleSchool(school.name)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors text-left ${
                isOpen ? 'hover:bg-gray-100' : 'opacity-50 hover:bg-gray-100'
              }`}
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0 border"
                style={{
                  backgroundColor: isOpen ? color : '#808080',
                  borderColor: isOpen ? color : '#999',
                }}
              />
              <span
                className={`text-xs font-medium flex-1 truncate ${
                  isOpen ? 'text-davis-charcoal' : 'text-gray-400 line-through'
                }`}
              >
                {school.name}
              </span>
              {isOpen && (
                <>
                  <span className="text-[10px] tabular-nums text-davis-slate w-10 text-right">
                    {students}
                  </span>
                  <span className="text-[10px] tabular-nums text-davis-slate w-12 text-right">
                    {distance.toFixed(2)}mi
                  </span>
                </>
              )}
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                  isOpen
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {isOpen ? 'On' : 'Off'}
              </span>
            </button>
          );
        })}
      </div>

      {closedSchools.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5">
          <div className="text-[10px] text-red-700">
            {closedSchools.map((s) => s.name).join(', ')}{' '}
            closed &mdash; students redistributed to nearest open school.
          </div>
        </div>
      )}
    </div>
  );
}
